import { XMarkIcon } from '@heroicons/react/24/outline';
import { AlertTriangleIcon } from '@langchain/untitled-ui-icons';
import Button from '@mui/joy/Button';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';

import { useInterruptMutation } from '@/hooks/useSwr';
import { HostProjectSchema } from '@/types/schema';

import { ExpandableErrorAlert } from '../ExpandableErrorAlert';

export function HostInterruptModal({
  hostProject,
  revisionId,
  isOpen,
  doClose,
}: {
  revisionId: string;
  hostProject: HostProjectSchema;
  isOpen: boolean;
  doClose: () => void;
}) {
  const interruptMutation = useInterruptMutation(
    { projectId: hostProject.id, revisionId },
    { onSuccess: doClose }
  );

  return (
    <Modal open={isOpen} onClose={() => doClose()}>
      <ModalDialog
        variant="outlined"
        role="alertdialog"
        aria-labelledby="alert-dialog-modal-title"
        aria-describedby="alert-dialog-modal-description"
        sx={{ maxWidth: 500 }}
      >
        <div className="flex flex-col items-start gap-3">
          <div className="flex w-full items-center justify-between">
            <div className="inline-flex items-center justify-center rounded-full bg-brand-tertiary p-1">
              <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-500 text-white">
                <AlertTriangleIcon className="size-4" />
              </div>
            </div>

            <button
              type="button"
              className="shrink-0 rounded-md p-0.5 text-tertiary transition-all hover:bg-secondary active:bg-secondary"
              onClick={() => doClose()}
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-semibold">Are you sure?</h2>
          </div>
        </div>

        <div className="mb-6 mt-3 flex flex-col gap-2">
          <p className="text-tertiary">
            Interrupted revisions have undefined behavior. This is only useful
            if you need to deploy a new revision, and you already have a
            revision hung in-progress.
          </p>
          <p className="text-tertiary">
            If your revision is{' '}
            <span className="font-semibold text-ls-orange-500">DEPLOYING</span>,
            then there is a chance that it could still become the active
            revision even after interruption.
          </p>
          <p className="text-tertiary">
            If in any other state, it will fail to become the active revision.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {interruptMutation.error && (
            <ExpandableErrorAlert error={interruptMutation.error} />
          )}

          <Button
            variant="solid"
            color="danger"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              interruptMutation.trigger();
            }}
            loading={interruptMutation.isMutating}
          >
            Interrupt revision
          </Button>
        </div>
      </ModalDialog>
    </Modal>
  );
}
